import config, { REACT_APP_HOMEPAGE_URL } from "../components/common/Config";
import { encryptPayload, resolveEncryptedResponse } from "./customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "./companySettingsCache";

const IMPERSONATION_PURPOSE = "admin_impersonation";

function pickRedirectUrl(payload) {
  if (!payload || typeof payload !== "object") return "";
  const candidates = [
    payload.redirect_url,
    payload.redirectUrl,
    payload.url,
    payload.customer_redirect_url,
    payload.data?.redirect_url,
    payload.data?.redirectUrl,
    payload.data?.url,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

function pickToken(payload) {
  if (!payload || typeof payload !== "object") return "";
  const candidates = [
    payload.token,
    payload.code,
    payload.one_time_token,
    payload.ott,
    payload.data?.token,
    payload.data?.code,
    payload.data?.one_time_token,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

function assertSafeRedirectUrl(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("Invalid redirect URL from server");
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error("Invalid redirect URL protocol");
  }
  const blocked = ["password", "pwd", "passwd", "jwt", "secret", "admin_password"];
  for (const key of blocked) {
    if (parsed.searchParams.has(key)) {
      throw new Error("Redirect URL must not include credentials");
    }
  }
  return parsed.toString();
}

function buildRedirectFromToken(token, customerSiteBase) {
  const base = String(customerSiteBase || "").replace(/\/$/, "");
  if (!base || !token) return "";
  return `${base}/auth/admin-impersonation?code=${encodeURIComponent(token)}`;
}

export async function createAdminCustomerImpersonationRedirect(customerId) {
  const id = String(customerId ?? "").trim();
  if (!id) {
    throw new Error("Customer id is required");
  }

  const bearer =
    localStorage.getItem("token") || localStorage.getItem("accessToken");
  if (!bearer) {
    throw new Error("Admin session expired. Please login again.");
  }

  const adminId = String(localStorage.getItem("userId") || "").trim();
  const settings = await getCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const formData = new FormData();
  const requestPayload = {
    customer_id: id,
    admin_id: adminId,
    purpose: IMPERSONATION_PURPOSE,
  };

  if (useEncrypt) {
    const encryptedPayload = encryptPayload(requestPayload);
    if (!encryptedPayload) {
      throw new Error("Failed to prepare secure request");
    }
    formData.append("encrypted_payload", encryptedPayload);
  } else {
    formData.append("customer_id", id);
    if (adminId) formData.append("admin_id", adminId);
    formData.append("purpose", IMPERSONATION_PURPOSE);
  }

  const response = await fetch(
    `${config.API_URL}/create_admin_customer_impersonation_token`,
    {
      method: "POST",
      headers: {
        authorization: `Bearer ${bearer}`,
      },
      body: formData,
    }
  );

  const raw = await response.json().catch(() => null);
  const data = resolveEncryptedResponse(raw) || raw;

  if (!response.ok) {
    throw new Error(
      data?.message || data?.error || "Failed to create view-customer token"
    );
  }

  if (data?.success === false || data?.status === false) {
    throw new Error(
      data?.message || data?.error || "Not allowed to view this customer"
    );
  }

  const payload = data?.data && typeof data.data === "object" ? data.data : data;
  let redirectUrl = pickRedirectUrl(payload);
  if (!redirectUrl) {
    const token = pickToken(payload);
    const siteBase =
      payload?.customer_site_url ||
      payload?.customerSiteUrl ||
      REACT_APP_HOMEPAGE_URL ||
      config.BASE_URL;
    redirectUrl = buildRedirectFromToken(token, siteBase);
  }

  if (!redirectUrl) {
    throw new Error("Server did not return a redirect URL or token");
  }

  return {
    redirectUrl: assertSafeRedirectUrl(redirectUrl),
    token: pickToken(payload) || null,
    expiresAt: payload?.expires_at || payload?.expiresAt || null,
    purpose: IMPERSONATION_PURPOSE,
  };
}

export { IMPERSONATION_PURPOSE };
